import { cva } from "class-variance-authority";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListFieldsRecordType, ListFieldsType, favoriteDocumentsListName, myDocumentsListName, favoriteFoldersListName } from "src/api/config";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import { getItems, getItemsSuccess } from "src/modules/spItems/actions";
import { getItemsRequest } from "src/modules/spItems/api";
import { selectGetStatus } from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { RootState } from "src/store/rootReducer";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import { usePagination } from "../../hooks/usePagination";
import { TableColumnWithIcon } from "../../pages/DashboardPage/acadia/mock";
import { EmptyList } from "../EmptyList";
import Loading from "../Loading";
import SortIcon from "../ui-icons/table/SortIcon";
import Pagination from "../ui/table/Pagination";
import { TableHeaderColumnProps } from "../ui/table/Table";

type TableHeadType = keyof Pick<
  ListFieldsType[typeof myDocumentsListName],
  "Code" | "Collection" | "Critical" | "Department" | "Process" | "Sector" | "DocType" | "Title"
>;

const tableHeaderContent2: Record<TableHeadType, TableColumnWithIcon> = {
  Code: {
    title: "Код",
    sort: true,
    style: { width: "100%" },
  },
  Title: {
    title: "Название документа",
    sort: true,
    style: { width: "100%" },
  },
  Collection: {
    title: "Коллекция",
    sort: true,
    style: { width: "100%" },
  },
  Process: {
    title: "Подразделение/Функция",
    sort: true,
    style: { width: "100%" },
  },
  Department: {
    title: "Колонна/Направление/Отдел",
    sort: true,
    style: { width: "100%" },
  },
  Sector: {
    title: "Блок колонны/Участок",
    sort: true,
    style: { width: "100%" },
  },
  DocType: {
    title: "Тип",
    sort: true,
    style: { width: "100%" },
  },
  Critical: {
    title: "Критичность",
    sort: true,
    style: { width: "100%" },
  },
};

function FavoriteTable() {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, status } = useGetSelect(myDocumentsListName);
  const user = useAppSelector((state) => selectCurrentUser(state));

  const { numberOfPages, pageItems, length: favoriteItemsCount } = usePagination(data, limit, page);

  function changePageHandler(page: number) {
    setPage(page);
  }

  useEffect(() => {
    async function fetchData() {
      if (user.id) {
        const documents: { items: ListFieldsRecordType[typeof favoriteDocumentsListName][] } = await getItemsRequest({
          listName: favoriteDocumentsListName,
          filters: { Author: user.id },
        });

        if (documents.items.length > 0) {
          const docIds = documents.items.map((item) => item.Document);

          dispatch(getItems({ listName: myDocumentsListName, filters: { ID: docIds } }));
        } else {
          dispatch(getItemsSuccess({ listName: myDocumentsListName, items: [], count: 0, nextPage: null }));
        }
      }
    }

    fetchData();
  }, [dispatch, user.id]);

  // useEffect(() => {
  //   if (status === "SUCCESS") {
  //     dispatch(getItemsIdle({ listName: myDocumentsListName }));
  //   }
  // }, [dispatch, status]);

  return (
    <Table
      tableName="Table with favorite documents"
      count={favoriteItemsCount}
      numberOfPages={numberOfPages}
      header={tableHeaderContent2}
      body={pageItems}
      page={page}
      limit={limit}
      onChangePage={changePageHandler}
    />
  );
}

export default FavoriteTable;

function Table({
  header,
  body,
  page,
  limit,
  count,
  numberOfPages,
  onChangePage,
  tableName,
}: {
  header: typeof tableHeaderContent2;
  body: ListFieldsType[typeof myDocumentsListName][];
  limit: number;
  count: number;
  page: number;
  numberOfPages: number;
  onChangePage: (page: number) => void;
  tableName: string;
}) {
  const [sortedField, setSortedField] = useState<keyof ListFieldsType[typeof myDocumentsListName] | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("default");

  const handleSort = (field: keyof ListFieldsType[typeof myDocumentsListName]) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (
        sortedField === "Collection" ||
        sortedField === "Department" ||
        sortedField === "Sector" ||
        sortedField === "DocType" ||
        sortedField === "Process"
      ) {
        return sortDirection === "asc"
          ? a[sortedField].Title.localeCompare(b[sortedField].Title)
          : b[sortedField].Title.localeCompare(a[sortedField].Title);
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (_.isObject(valueA) && _.isString(valueA) && _.isString(valueB) && sortedField === "Code") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }
      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body]) as ListFieldsType[typeof myDocumentsListName][];

  return (
    <FlexRow>
      <FlexCol className="w-full">
        <TableList
          head={header}
          body={sortedTableBodyContent}
          sort={handleSort}
          sortDirection={sortDirection}
          sortedField={sortedField}
          tableName={tableName}
        />
        {count > limit && <Pagination numberOfPages={numberOfPages} page={page} onChange={onChangePage} />}
      </FlexCol>
    </FlexRow>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  tableName,
}: {
  head: Omit<typeof tableHeaderContent2, "Author" | "ID">;
  body: ListFieldsType[typeof myDocumentsListName][];
  sort: (f: keyof ListFieldsType[typeof myDocumentsListName]) => void;
  sortedField: keyof ListFieldsType[typeof myDocumentsListName] | null;
  sortDirection: "asc" | "desc" | "default";
  tableName: string;
}) {
  const status = useAppSelector((state: RootState) => selectGetStatus(state, myDocumentsListName));
  const { data: favoriteFolders, status: favoriteFoldersStatus } = useGetSelect(favoriteFoldersListName);

  const [query, setQuery] = useSearchParams();

  function openDocumentCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("documentCardMobile", id.toString());

    setQuery(newSearchParams);
  }

  return (
    <FlexRow className="overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB]">
      <FlexCol className="divide-y w-full grow">
        {status === "SUCCESS" ? (
          Array.isArray(body) && body.length > 0 ? (
            body.map((item) => (
              <FlexCol
                className="px-4 py-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 cursor-pointer gap-x-2"
                key={item.ID}
                onClick={() => openDocumentCard(item.ID)}
              >
                <FlexRow className="break-words text-darkblue gap-[5px] text-[13px]" style={{ width: "100%" }}>
                  <CriticallyListItem item={item.Critical} />
                  {item.Title}
                </FlexRow>
                <FlexCol className="break-words text-[#637381] text-[13px]" style={{ width: "100%" }}>
                  {`${item.Code}.${item.Index}`}
                </FlexCol>
                <FlexCol className="break-words text-[#637381] text-[13px]" style={{ width: "100%" }}>
                  {item.Department.Title}/{item.Sector.Title}/{item.Process.Title}/{item.Collection.Title}/{item.DocType.Title}
                </FlexCol>
              </FlexCol>
            ))
          ) : favoriteFoldersStatus === "SUCCESS" && favoriteFolders.length === 0 ? (
            <EmptyList title="Здесь будут храниться документы, которые вы добавили в избранное" />
          ) : null
        ) : (
          <div className="p-4">
            <Loading />
          </div>
        )}
      </FlexCol>
    </FlexRow>
  );
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

const criticalItemStyle = cva("block h-2 w-2 rounded-full", {
  variants: {
    active: {
      true: "bg-[#BA0C2F]",
      false: "bg-[#8899A8]",
    },
  },
  defaultVariants: {
    active: false,
  },
});

export function CriticallyListItem({ item }: { item: boolean }) {
  return (
    <FlexRow className="items-center space-x-1">
      <FlexCol>
        <span className={twMerge(criticalItemStyle({ active: item }))}></span>
      </FlexCol>
    </FlexRow>
  );
}
